import { IconArrowDown, IconArrowUp } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type PerformanceKpi,
  type PerformanceTrendPoint,
  parsePerformanceSlots,
  setPerformanceSlot,
} from "@/components/shared/performance/performance";
import { Skeleton } from "@/components/ui/skeleton";
import type { DashboardPerformanceMetric } from "@/lib/dashboard-sales";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";
import { KpiMetricSelect } from "./kpi-metric-select";
import {
  DASHBOARD_METRIC_KEYS,
  type DashboardCompareMode,
  DEFAULT_KPI_SLOTS,
  type DeltaTone,
  formatMetricValue,
  kpiDelta,
  METRIC_DOT_COLOR,
} from "./kpi-strip-metrics";

type Metric = DashboardPerformanceMetric;

export interface KpiStripProps {
  kpis: readonly PerformanceKpi<Metric>[];
  trend: readonly PerformanceTrendPoint<Metric>[];
  /** Metric shown on each card, in strip order (six by default). */
  slots: readonly Metric[];
  onSlotsChange: (slots: Metric[]) => void;
  compare: DashboardCompareMode;
  getLabel: (metric: Metric) => string;
  className?: string;
}

/**
 * Read-only KPI strip. The Performance card owns what is charted; a card's label
 * is only a metric select, so the strip stays a personal summary of six numbers.
 */
export function KpiStrip({
  kpis,
  trend,
  slots,
  onSlotsChange,
  compare,
  getLabel,
  className,
}: KpiStripProps): React.ReactElement {
  const kpiByMetric = useMemo(() => new Map(kpis.map((kpi) => [kpi.key, kpi])), [kpis]);
  const isDefault =
    slots.length === DEFAULT_KPI_SLOTS.length &&
    slots.every((metric, index) => metric === DEFAULT_KPI_SLOTS[index]);

  function swapMetric(index: number, metric: Metric): void {
    const nextSlots = setPerformanceSlot(slots, index, metric);
    if (nextSlots[index] === metric) onSlotsChange(nextSlots);
  }

  return (
    <div
      data-slot="kpi-strip"
      className={cn(
        "grid min-w-0 grid-cols-2 gap-px overflow-hidden rounded-lg bg-line shadow-[var(--paper-raised-shadow)] md:grid-cols-3 xl:grid-cols-6",
        className,
      )}
    >
      {slots.map((metric, index) => (
        <KpiCard
          // Cards are fixed positions; keying by slot keeps focus on the select after a swap.
          // biome-ignore lint/suspicious/noArrayIndexKey: the slot index is the card's identity
          key={index}
          metric={metric}
          kpi={kpiByMetric.get(metric)}
          trend={trend}
          slots={slots}
          kpis={kpiByMetric}
          compare={compare}
          getLabel={getLabel}
          onMetricChange={(next) => swapMetric(index, next)}
          onReset={isDefault ? undefined : () => onSlotsChange([...DEFAULT_KPI_SLOTS])}
        />
      ))}
    </div>
  );
}

interface KpiCardProps {
  metric: Metric;
  kpi: PerformanceKpi<Metric> | undefined;
  trend: readonly PerformanceTrendPoint<Metric>[];
  slots: readonly Metric[];
  kpis: ReadonlyMap<Metric, PerformanceKpi<Metric>>;
  compare: DashboardCompareMode;
  getLabel: (metric: Metric) => string;
  onMetricChange: (metric: Metric) => void;
  onReset: (() => void) | undefined;
}

function KpiCard({
  metric,
  kpi,
  trend,
  slots,
  kpis,
  compare,
  getLabel,
  onMetricChange,
  onReset,
}: KpiCardProps): React.ReactElement {
  const { formatNumber } = useFormatters();

  return (
    <div className="@container flex min-w-0 flex-col gap-2 overflow-clip bg-surface py-3.5 pr-3.5 pl-2">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <KpiMetricSelect
          value={metric}
          slots={slots}
          kpis={kpis}
          getLabel={getLabel}
          onValueChange={onMetricChange}
          onReset={onReset}
          dotColor={METRIC_DOT_COLOR[metric] ?? "var(--color-chart-neutral)"}
        />
      </div>

      {/* Six cards share ~960px at a 1280px viewport; values step down a size once the content box drops below 150px. */}
      <div className="min-w-0 pl-2">
        {!kpi || kpi.loading ? (
          <Skeleton className="my-1 h-6 w-24" />
        ) : (
          <div className="truncate text-2xl font-semibold tracking-tight tabular-nums text-ink @max-[150px]:text-xl">
            {formatMetricValue(metric, kpi.value, kpi.currency, { formatNumber })}
          </div>
        )}
      </div>

      <div className="flex h-5 min-w-0 items-center justify-between gap-2 pl-2">
        {compare === "off" || !kpi || kpi.loading ? <span /> : <KpiDeltaLabel kpi={kpi} />}
        <Sparkline
          values={trend.map((point) => point[metric] ?? null)}
          color="var(--color-chart-neutral)"
        />
      </div>
    </div>
  );
}

const DELTA_TONE_CLASS: Record<DeltaTone, string> = {
  improvement: "text-success-foreground",
  worsening: "text-destructive-foreground",
  neutral: "text-text-secondary",
};

function KpiDeltaLabel({ kpi }: { kpi: PerformanceKpi<Metric> }): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const { formatNumber } = useFormatters();
  const delta = kpiDelta(kpi);
  if (!delta) {
    return (
      <span
        className="text-xs font-medium text-muted-foreground"
        title={t("overview.kpi.noComparison")}
      >
        —<span className="sr-only">{t("overview.kpi.noComparison")}</span>
      </span>
    );
  }
  const magnitude =
    delta.kind === "points"
      ? t("overview.kpi.deltaPoints", {
          value: formatNumber(delta.magnitude, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }),
        })
      : formatNumber(delta.magnitude, {
          style: "percent",
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        });
  const Arrow = delta.direction === "down" ? IconArrowDown : IconArrowUp;
  return (
    <span
      className={cn(
        "flex min-w-0 items-center gap-0.5 text-xs font-medium tabular-nums",
        DELTA_TONE_CLASS[delta.tone],
      )}
    >
      {delta.direction === "flat" ? null : (
        <span className="flex size-3 shrink-0 items-center justify-center" aria-hidden>
          <Arrow className="size-3" stroke={2} />
        </span>
      )}
      <span className="sr-only">{t(`overview.kpi.direction.${delta.direction}`)}</span>
      <span className="truncate">{magnitude}</span>
    </span>
  );
}

const SPARK_WIDTH = 56;
const SPARK_HEIGHT = 20;

/** 56×20 trend line; gaps (null buckets) are skipped rather than drawn as zero. */
function Sparkline({
  values,
  color,
}: {
  values: readonly (number | null)[];
  color: string;
}): React.ReactElement {
  const points = values.flatMap((value, index) => (value == null ? [] : [{ value, index }]));
  let path = "";
  if (points.length > 1) {
    const min = Math.min(...points.map((point) => point.value));
    const max = Math.max(...points.map((point) => point.value));
    const span = max - min || 1;
    const lastIndex = Math.max(values.length - 1, 1);
    path = points
      .map(({ value, index }, order) => {
        const x = 1 + (index / lastIndex) * (SPARK_WIDTH - 2);
        // Flat series sit mid-height; otherwise use the 4–16px band like the design.
        const y = max === min ? SPARK_HEIGHT / 2 : 16 - ((value - min) / span) * 12;
        return `${order === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");
  }
  return (
    <svg
      width={SPARK_WIDTH}
      height={SPARK_HEIGHT}
      viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
      aria-hidden="true"
      className="shrink-0"
    >
      {path ? (
        <path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : null}
    </svg>
  );
}

/** Card metric choices persist per browser; malformed or stale values fall back to the default row. */
export function useDashboardKpiSlots(
  storageKey = "adpraxis:dashboard-kpi-slots:v1",
): [Metric[], (slots: Metric[]) => void] {
  const [slots, setSlots] = useState<Metric[]>(() =>
    parsePerformanceSlots(readStorage(storageKey), DASHBOARD_METRIC_KEYS, DEFAULT_KPI_SLOTS),
  );
  useEffect(() => {
    writeStorage(storageKey, JSON.stringify(slots));
  }, [slots, storageKey]);
  const update = useCallback((next: Metric[]) => setSlots(next), []);
  return [slots, update];
}

function readStorage(key: string): string | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage.getItem(key);
  } catch {
    // Restricted browser contexts can deny storage access.
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // The choice still applies for this session.
  }
}
